import React, { useEffect, useState, ChangeEvent } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import { unstable_batchedUpdates } from 'react-dom';

import { RootState } from '~/redux/store';

import { ReactComponent as Search } from '~/utils/images/search.svg';
import Breadcrumbs from '~/components/Breadcrumbs/Breadcrumbs';
import Spinner from '~/components/Spinner/Spinner';
import { createAdminsRequest, importPMs, ImportPMResult } from '~/api/admin';
import {
  fetchAdminsPageRequest,
  updateAdminBuildingsRequest,
  updateAdminRoleRequest
} from '~/api/admin';
import AdminTable from '~/components/AdminTable/AdminTable';
import CobuSelect from '~/components/CobuSelect/CobuSelect';
import ServerPagination from '~/components/ServerPagination/ServerPagination';

import classes from './Admins.module.css';
import { AdminUser, AdminBuildingType } from '~/types/admin';
import { Roles } from '~/enums/Roles';
import { SelectType } from '~/enums/SelectType';
import { SelectedOptionType } from '~/types/form';
import GenericModal from '~/components/GenericModal/GenericModal';
import AdminDetails from '~/components/AdminDetails/AdminDetails';
import CsvImportModal from '~/components/CsvImportModal/CsvImportModal';

const DEBOUNCE_MS = 300;

const Admins = () => {
  const loggedInUser = useSelector((state: RootState) => state.auth.user);
  const [searchRole, setSearchRole] = useState('');
  const [searchNameBuilding, setSearchNameBuilding] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [showChangeRoleModal, setShowChangeRoleModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<AdminUser>();
  const [selectedRoleToChange, setSelectedRoleToChange] = useState('');
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  // assign a role to a user
  const [selectedRole, setSelectedRole] = useState<SelectedOptionType | null>({
    label: '',
    value: ''
  });
  const [
    selectedBuilding,
    setSelectedBuilding
  ] = useState<SelectedOptionType | null>({
    label: '',
    value: ''
  });
  const [emails, setEmails] = useState(['']);
  const [emailError, setEmailError] = useState('');
  const [selectedAdminId, setSelectedAdminId] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedBuildings, setSelectedBuildings] = useState<
    AdminBuildingType[]
  >([]);
  const [
    changeAdminBuildingsDisabled,
    setChangeAdminBuildingsDisabled
  ] = useState(true);
  const [addAdminDisabled, setAddAdminDisabled] = useState(true);
  const [showImportPMsModal, setShowImportPMsModal] = useState(false);
  const trimmedSearch = searchNameBuilding.trim();
  useEffect(() => {
    if (trimmedSearch === q) return;
    const timer = setTimeout(() => {
      // React 16 only batches inside event handlers; one render = one request.
      unstable_batchedUpdates(() => {
        setQ(trimmedSearch);
        setPage(1);
      });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [trimmedSearch, q]);

  const { data, isLoading, refetch } = useQuery(
    ['admins-page', page, pageSize, q, searchRole],
    () =>
      fetchAdminsPageRequest({
        page,
        pageSize,
        q: q || undefined,
        role: searchRole || undefined
      }).then((res) => res.data),
    { keepPreviousData: true, refetchOnWindowFocus: false }
  );
  const admins = data?.admins ?? [];
  const pageCount = Math.max(1, Math.ceil((data?.total ?? 0) / pageSize));

  // Past the last page the server reports total 0, so this lands on page 1.
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const onDetailsClickHandler = (user: AdminUser, role: string) => {
    setShowChangeRoleModal(true);
    setSelectedAdmin(user);
    setSelectedRoleToChange(role);
  };

  const onAdminRoleChangeHandler = () => {
    updateAdminRoleRequest(selectedRoleToChange, selectedAdmin!.uuid).then(
      () => refetch()
    );
    setShowChangeRoleModal(false);
  };

  const onChangeAdminBuildings = () => {
    updateAdminBuildingsRequest({
      user: selectedAdminId,
      buildings: selectedBuildings.map((b) => b.uuid)
    }).then(() => {
      refetch();
      setShowDetailsModal(false);
    });
  };
  const createAdminsFunction = () => {
    const emailsReq = emails.filter((email) => email != '');
    setEmails(emailsReq);
    createAdminsRequest(emailsReq, selectedBuilding!.value, selectedRole!.value)
      .then(() => {
        refetch();
        setEmails(['']);
        setAddAdminDisabled(true);
        setShowAddUserModal(false);
      })
      .catch((data) => {
        setEmailError(data.message);
        setAddAdminDisabled(true);
      });
  };
  const addEmailField = () => {
    setEmails([...emails, '']);
  };

  const onRemoveBuildingHandler = (buildingId: string) => {
    const selectedBuildingsReduced = [...selectedBuildings];
    const index = selectedBuildings.findIndex((b) => b.uuid === buildingId);
    selectedBuildingsReduced.splice(index, 1);
    setSelectedBuildings(selectedBuildingsReduced);
    setChangeAdminBuildingsDisabled(false);
  };

  const onAddBuildingHandler = (building: AdminBuildingType) => {
    const selectedBuildingsCopy = [
      ...selectedBuildings,
      { uuid: building.uuid, name: building.name }
    ];
    setSelectedBuildings(selectedBuildingsCopy);
    setChangeAdminBuildingsDisabled(false);
  };

  return (
    <div className='content-container'>
      {isLoading && <Spinner />}
      {!isLoading && (
        <div>
          <Breadcrumbs />
          <div className={classes.container}>
            <h1 className='h1-title'>User / Roles management</h1>
            <div className={`${classes.filterSearchContainer}`}>
              <div className={`${classes.search} pointer margin-right-16`}>
                <div
                  className={`${classes.searchIconContainer} margin-left-16 margin-right-8`}
                >
                  <Search />
                </div>
                <div
                  className={`${classes.searchBy} margin-top-8 margin-right-8`}
                >
                  <input
                    className='search-input margin-top-16'
                    placeholder='Search by Name, Email or Building'
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setSearchNameBuilding(event.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
              <div className={`${classes.search} margin-right-16`}>
                <p className={'margin-left-16 font-16 font-style-normal'}>
                  Filter by:
                </p>
                <CobuSelect
                  options={[
                    { label: Roles.Vcm, value: Roles.Vcm },
                    { label: Roles.Pm, value: Roles.Pm },
                    { label: Roles.PM_FREE, value: Roles.PM_FREE },
                    { label: 'ALL ROLES', value: '' }
                  ]}
                  selectType={SelectType.Filter}
                  onChange={(selectedOption: SelectedOptionType | null) => {
                    setSearchRole(selectedOption!.value);
                    setPage(1);
                  }}
                  isSearchable={false}
                  placeholder='Role'
                />
              </div>
              <button
                className={`${classes.addUserButton} btn-primary btn-standard`}
                onClick={() => {
                  setEmails(['']);
                  setShowAddUserModal(true);
                }}
              >
                Add a user
              </button>
              <button
                className={`${classes.addUserButton} btn-secondary btn-standard margin-left-16`}
                onClick={() => setShowImportPMsModal(true)}
              >
                Import PMs
              </button>
            </div>
          </div>
          <AdminTable
            data={admins}
            onAdminRoleChange={onDetailsClickHandler}
            loggedInUserUuid={loggedInUser.uuid}
            onDetailsClick={(userId: string) => {
              setSelectedAdminId(userId);
              setShowDetailsModal(true);
              setChangeAdminBuildingsDisabled(true);
              const buildings = admins.filter(
                (admin) => admin.user.uuid === userId
              )[0].buildings;
              setSelectedBuildings(buildings);
            }}
          />
          <ServerPagination
            page={page}
            pageCount={pageCount}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
          {showImportPMsModal && (
            <CsvImportModal<ImportPMResult>
              title='Import PMs from CSV'
              intro='One PM per row. Separate properties with ";" and include home_property. Already-imported PMs are skipped.'
              requiredColumns={[
                'first_name',
                'email',
                'home_property',
                'properties'
              ]}
              templateColumns={[
                'first_name',
                'last_name',
                'email',
                'phone',
                'room',
                'account_type',
                'home_property',
                'initial_password',
                'properties'
              ]}
              templateRow='Harrison,Urby,pm@example.com,9999999999,Cobu,PM,Harrison Urby,,Harrison Urby;Example Property 2'
              templateFilename='cobu-pms-template.csv'
              resultsFilename='pm-import-results.csv'
              columns={[
                { header: 'first_name', value: (r) => r.firstName },
                { header: 'last_name', value: (r) => r.lastName },
                { header: 'email', value: (r) => r.email },
                { header: 'phone', value: (r) => r.phone },
                { header: 'room', value: (r) => r.room },
                { header: 'account_type', value: (r) => r.role },
                { header: 'home_property', value: (r) => r.homeProperty },
                { header: 'properties', value: (r) => r.properties }
              ]}
              importRows={importPMs}
              onClose={(changedAny) => {
                setShowImportPMsModal(false);
                if (changedAny) refetch();
              }}
            />
          )}
          {showChangeRoleModal && (
            <GenericModal
              showModal={showChangeRoleModal}
              title={'Change User Role?'}
              actionFunction={onAdminRoleChangeHandler}
              actionText={'Change'}
              onChange={(value) => setShowChangeRoleModal(value)}
              content={
                <div>
                  Are you sure you want to change role of the user{' '}
                  {selectedAdmin?.firstName} {selectedAdmin?.lastName} to{' '}
                  {selectedRoleToChange}?
                </div>
              }
            />
          )}
          <GenericModal
            showModal={showAddUserModal}
            title={'Assign a Role'}
            content={
              <div>
                <div className='margin-top-16 modal-width'>
                  <label>Role</label>
                  <div className='margin-top-4'>
                    <CobuSelect
                      options={[
                        { label: Roles.Vcm, value: Roles.Vcm },
                        { label: Roles.Superadmin, value: Roles.Superadmin },
                        { label: Roles.Pm, value: Roles.Pm },
                        { label: Roles.PM_FREE, value: Roles.PM_FREE }
                      ]}
                      onChange={(selectedOption: SelectedOptionType | null) => {
                        if (
                          selectedBuilding?.value !== '' &&
                          emails[0] !== ''
                        ) {
                          setAddAdminDisabled(false);
                        }
                        setSelectedRole(selectedOption);
                      }}
                      selectType={SelectType.Form}
                    />
                  </div>
                </div>
                <div className='margin-top-16'>
                  <label>Building</label>
                  <div className='margin-top-4'>
                    <CobuSelect
                      asyncBuildings
                      onChange={(selectedOption: SelectedOptionType | null) => {
                        if (selectedRole?.value !== '' && emails[0] !== '') {
                          setAddAdminDisabled(false);
                        }
                        setSelectedBuilding(selectedOption);
                      }}
                      selectType={SelectType.Form}
                    />
                  </div>
                </div>
                <div className='margin-top-16'>
                  <label>Email</label>
                  {Array.apply(null, Array(emails.length)).map((n, i) => (
                    <>
                      <input
                        className={`email-txt-area input`}
                        type='text'
                        id={`email ${i}`}
                        name='email'
                        value={emails[i]}
                        onChange={(e) => {
                          let newArr = [...emails];
                          newArr[i] = e.target.value;
                          setEmails(newArr);
                          if (
                            selectedRole?.value !== '' &&
                            selectedBuilding?.value !== '' &&
                            emailError === ''
                          ) {
                            setAddAdminDisabled(false);
                          }
                          setEmailError('');
                        }}
                      />
                    </>
                  ))}
                </div>
                {emailError && <p className='form-error '>{emailError}</p>}

                <button
                  className={'btn btn-standard btn-secondary'}
                  onClick={() => addEmailField()}
                >
                  Add new Email
                </button>
              </div>
            }
            actionFunction={() => createAdminsFunction()}
            actionText={'Save'}
            onChange={(value) => {
              setShowAddUserModal(value);
              setAddAdminDisabled(true);
              setEmailError('');
              setEmails(['']);
            }}
            actionButtonDisabled={addAdminDisabled}
          />
        </div>
      )}
      {selectedAdminId !== '' && (
        <GenericModal
          showModal={showDetailsModal}
          title={'User Details'}
          actionText={'Save'}
          actionFunction={onChangeAdminBuildings}
          onChange={(value: boolean) => {
            setSelectedAdminId('');
            setShowDetailsModal(value);
          }}
          actionButtonDisabled={changeAdminBuildingsDisabled}
          content={
            <div>
              <AdminDetails
                data={selectedBuildings}
                onRemoveBuilding={(buildingId: string) =>
                  onRemoveBuildingHandler(buildingId)
                }
                onAddBuilding={onAddBuildingHandler}
                role={
                  admins.filter(
                    (admin) => admin.user.uuid === selectedAdminId
                  )[0]?.role
                }
                selectedBuildingIds={selectedBuildings.map((b) => b.uuid)}
              />
            </div>
          }
          actionButtonsInHeader={true}
        />
      )}
    </div>
  );
};

export default Admins;
